import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { Patient, patientFlagsToArray } from './types';
import { HelpCircle } from 'lucide-react';

type PatientEditModalProps = {
  patient: Patient;
  trigger: React.ReactNode;
  onSuccess?: (updated: Patient) => void;
  availableFlags?: {
    neurodivergence: Record<string, string>;
    psychological_state: Record<string, string>;
  };
};

function normalizeErrors(errors: unknown): string[] {
  if (!errors) return ['Errore di validazione'];
  if (Array.isArray(errors)) return errors.map(String);
  if (typeof errors === 'string') return [errors];
  if (typeof errors === 'object' && errors !== null) {
    const errObj = errors as Record<string, unknown>;
    if (Array.isArray(errObj.full_messages)) return errObj.full_messages.map(String);
    return Object.values(errObj).flat().map(String);
  }
  return ['Errore di validazione'];
}

export default function PatientEditModal({
  patient,
  trigger,
  onSuccess,
  availableFlags = {
    neurodivergence: {},
    psychological_state: {},
  },
}: PatientEditModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: patient.first_name ?? '',
    last_name: patient.last_name ?? '',
    email: patient.email,
    prompt: patient.prompt ?? '',
    profile: patient.profile ?? '',
    flags: patientFlagsToArray(patient.flags),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[] | undefined>(undefined);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFlagChange = (flagCode: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      flags: checked ? [...prev.flags, flagCode] : prev.flags.filter((f: string) => f !== flagCode),
    }));
  };

  const isFlagChecked = (flagCode: string) => {
    return formData.flags.includes(flagCode);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors(undefined);
    router.patch(
      `/therapists/patients/${patient.id}`,
      { patient: formData },
      {
        preserveState: true,
        onSuccess: () => {
          setIsOpen(false);
          setIsSubmitting(false);
          if (onSuccess) onSuccess({ ...patient, ...formData });
          router.reload({
            only: ['patient', 'overview', 'clinicalNotesMeta', 'availableFlags'],
          });
        },
        onError: (page: unknown) => {
          setIsSubmitting(false);
          let extracted = page;
          if (typeof page === 'object' && page !== null && 'props' in page) {
            const props = (page as { props?: { errors?: unknown } }).props;
            if (props && 'errors' in props) {
              extracted = props.errors;
            }
          }
          setErrors(normalizeErrors(extracted));
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl md:max-w-6xl max-h-[80vh] overflow-y-auto"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          setTimeout(() => firstFieldRef.current?.focus(), 0);
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Modifica cliente</DialogTitle>
          <DialogDescription>Modifica i dati del cliente e salva per aggiornare.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="first_name" className="text-right">
                Nome
              </Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('first_name', e.target.value)
                }
                className="col-span-3"
                required
                ref={firstFieldRef}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="last_name" className="text-right">
                Cognome
              </Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('last_name', e.target.value)
                }
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('email', e.target.value)
                }
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="profile" className="text-right pt-2">
                <span className="inline-flex items-center justify-end gap-2">
                  <span>Profilo cliente (opzionale)</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Mostra esempio profilo cliente"
                        >
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-sm">
                        Esempio: &quot;Donna 34 anni, lavoro stressante, obiettivo ridurre ansia;
                        preferisce comunicazione diretta e sintetica.&quot;
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </Label>
              <div className="col-span-3 space-y-2">
                <Textarea
                  id="profile"
                  value={formData.profile}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('profile', e.target.value)
                  }
                  className="min-h-[100px]"
                  placeholder="Descrizione sintetica del profilo del cliente"
                />
                <p className="text-xs text-muted-foreground">
                  Usa questo spazio per annotare il profilo del cliente. Verrà usato anche
                  dall&apos;IA per migliorare la conversazione.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="prompt" className="text-right pt-2">
                <span className="inline-flex items-center justify-end gap-2">
                  <span>Istruzioni per l&apos;IA (opzionale)</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Mostra esempio istruzioni per l'IA"
                        >
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-sm">
                        Esempio: &quot;Usa tono calmo, fai domande brevi, evita diagnosi o
                        consigli clinici, concentra il focus su coping e grounding.&quot;
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </Label>
              <div className="col-span-3 space-y-2">
                <Textarea
                  id="prompt"
                  value={formData.prompt}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('prompt', e.target.value)
                  }
                  className="min-h-[100px]"
                  placeholder="Note utili per personalizzare l'esperienza di chat del cliente"
                />
                <p className="text-xs text-muted-foreground">
                  Se vuoi puoi fornire istruzioni direttamente all&apos;IA su come gestire le
                  conversazioni con il cliente.
                </p>
              </div>
            </div>

            {/* Neurodivergence Flags */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Neurodivergenza</Label>
              <div className="col-span-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(availableFlags.neurodivergence).map(([code, displayText]) => (
                    <div key={code} className="flex items-center space-x-2">
                      <Checkbox
                        id={`neurodivergence-${code}`}
                        checked={isFlagChecked(code)}
                        onCheckedChange={(checked: boolean) => handleFlagChange(code, checked)}
                      />
                      <Label htmlFor={`neurodivergence-${code}`} className="text-sm font-normal">
                        {displayText}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Psychological State Flags */}
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Stato Psicologico</Label>
              <div className="col-span-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(availableFlags.psychological_state).map(([code, displayText]) => (
                    <div key={code} className="flex items-center space-x-2">
                      <Checkbox
                        id={`psychological_state-${code}`}
                        checked={isFlagChecked(code)}
                        onCheckedChange={(checked: boolean) => handleFlagChange(code, checked)}
                      />
                      <Label
                        htmlFor={`psychological_state-${code}`}
                        className="text-sm font-normal"
                      >
                        {displayText}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {errors && errors.length > 0 && (
            <div className="text-sm text-red-600 space-y-1">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
